import { CommandExecutionContext } from 'types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from 'types/standardToolResults';
import { SimpleDocCommand } from '../../../DocsApp/commands/v5/types';
import { createFileLogger } from '../../../../utils/logger';
import { entityResolver } from '../../../../services/entityResolver';
import { createDocumentReader } from '../../../DocsApp/services/doc/DocumentReader';
import { ttsService } from '../../services/ttsService';

const log = createFileLogger('SPEAK_OUTLOUD');

export const speakOutloudCommand: SimpleDocCommand = {
  id: 'speak_outloud',
  description: `Read document or text content aloud using natural text-to-speech

**PARAMETERS:**
• id (optional): Document ID to read (e.g. "doc_large_pig_coconut" or "file_dark_star_dog")
• text (optional): Raw text to read (alternative to document)
• selector (optional): What to read from document (same as doc_read)
  - "all" (default): Entire document
  - {"indexRange": {"from": 0, "to": 10}}: Specific node range
  - {"range": {"from": "node_id", "to": "node_id"}}: Node ID range  
  - {"query": "search term"}: Search results
• voice (optional): Voice to use - alloy, echo, fable, onyx, nova, shimmer (default: alloy)
• speed (optional): Reading speed 0.25-4.0 (default: 1.0)
• quality (optional): "standard" or "hd" (default: "standard")

**CONSTRAINTS:**
• Maximum 100,000 characters per request
• If text exceeds limit, will return error asking to use selector for smaller section
• Audio generation may take 10-30 seconds for long documents
• Returns base64-encoded MP3 audio`,
  
  logicalName: 'speak_outloud',
  version: 'v1',
  appType: 'listen',
  
  formatViewData: (result: any, _toolInput: any) => {
    try {
      if (result?.data?.type === 'audio_content') {
        const data = result.data;
        return {
          type: 'audio_content',
          summary: result.message || `Generated audio for ${data.documentName || 'text'}`,
          data: {
            documentName: data.documentName,
            duration: data.duration,
            voice: data.voice,
            speed: data.speed,
            quality: data.quality,
            characterCount: data.characterCount,
            chunks: data.chunks
          },
          meta: {
            duration: result.metadata?.executionTime
          }
        };
      }
      
      return {
        type: 'tool_summary',
        summary: result?.message || 'Generated audio',
        data: result?.data || {}
      };
    } catch (error) {
      console.warn('[SPEAK_OUTLOUD] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Generated audio',
        data: {}
      };
    }
  },
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID to read aloud',
        required: false
      },
      {
        name: 'text',
        type: 'string',
        description: 'Raw text to read (alternative to document)',
        required: false
      },
      {
        name: 'selector',
        type: 'string',
        description: 'What to read from document (same format as doc_read)',
        required: false,
        defaultValue: 'all'
      },
      {
        name: 'voice',
        type: 'string',
        description: 'Voice: alloy, echo, fable, onyx, nova, shimmer',
        required: false,
        defaultValue: 'alloy'
      },
      {
        name: 'speed',
        type: 'number',
        description: 'Reading speed 0.25-4.0',
        required: false,
        defaultValue: 1.0
      },
      {
        name: 'quality',
        type: 'string',
        description: 'Quality: standard or hd',
        required: false,
        defaultValue: 'standard'
      }
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      let textToRead: string;
      let documentName = 'text';
      
      // OPTION 1: Read from document
      if (params.id) {
        log.log('[SPEAK_OUTLOUD] Reading from document:', params.id);
        
        try {
          context.onProgress?.({ type: 'streaming_update', activity: 'Loading document...' });
          
          const resolved = await entityResolver.resolveEntity(params.id, context);
          documentName = resolved.entity.name || 'document';
          
          const { reader, error: readerError } = await createDocumentReader(params.id, context);
          if (readerError || !reader) {
            return createErrorResult('Document not accessible', readerError || `Could not access document "${documentName}"`);
          }
          
          // Handle selector (same logic as doc_read)
          const selector = params.selector || 'all';
          
          if (selector === 'all') {
            const content = reader.readAll();
            textToRead = content.nodes.map((n: any) => n.text).join('\n\n');
          } else if (typeof selector === 'object' || (typeof selector === 'string' && selector.startsWith('{'))) {
            // Parse JSON selector
            const parsedSelector = typeof selector === 'string' ? JSON.parse(selector) : selector;
            
            // Handle indexRange selector
            if (parsedSelector.indexRange) {
              const allContent = reader.readAll();
              const { from, to, start, count } = parsedSelector.indexRange;
              const fromIndex = from !== undefined ? from : start;
              const toIndex = to !== undefined ? to : (start + count - 1);
              
              const rangeNodes = allContent.nodes.slice(fromIndex, toIndex + 1);
              textToRead = rangeNodes.map((n: any) => n.text).join('\n\n');
            }
            // Handle range selector (by node ID)
            else if (parsedSelector.range) {
              const allContent = reader.readAll();
              const { from, to, start, count } = parsedSelector.range;
              const startNodeId = from || start;
              
              const startIndex = allContent.nodes.findIndex((n: any) => n.id === startNodeId);
              if (startIndex === -1) {
                return createErrorResult('Start node not found', `Node "${startNodeId}" not found in document.`);
              }
              
              let endIndex: number;
              if (count !== undefined) {
                endIndex = Math.min(startIndex + count - 1, allContent.nodes.length - 1);
              } else {
                endIndex = allContent.nodes.findIndex((n: any) => n.id === to);
                if (endIndex === -1) {
                  return createErrorResult('End node not found', `Node "${to}" not found in document.`);
                }
              }
              
              const rangeNodes = allContent.nodes.slice(startIndex, endIndex + 1);
              textToRead = rangeNodes.map((n: any) => n.text).join('\n\n');
            }
            // Handle query selector
            else if (parsedSelector.query !== undefined) {
              const searchResult = reader.search(parsedSelector.query, { fuzzy: true, limit: 999999 });
              if (!searchResult.nodes || searchResult.nodes.length === 0) {
                return createErrorResult('No matches found', `No nodes found containing "${parsedSelector.query}".`);
              }
              textToRead = searchResult.nodes.map((n: any) => n.text).join('\n\n');
            } else {
              return createErrorResult('Invalid selector', 'Selector must be "all" or a valid JSON selector object');
            }
          } else {
            // Treat as "all"
            const content = reader.readAll();
            textToRead = content.nodes.map((n: any) => n.text).join('\n\n');
          }
        } catch (e: any) {
          log.error('[SPEAK_OUTLOUD] Error reading document:', e);
          return createErrorResult('Failed to read document', e.message || 'Unknown error');
        }
      }
      // OPTION 2: Read raw text
      else if (params.text) {
        textToRead = params.text;
        documentName = 'text';
      }
      else {
        return createErrorResult('Missing input', 'Provide either "id" (document) or "text" parameter');
      }
      
      // Validate text length
      const charCount = textToRead.trim().length;
      if (charCount === 0) {
        return createErrorResult('No content', 'Document or text is empty');
      }
      
      if (charCount > 100000) {
        return createErrorResult(
          'Content too long',
          `Text is ${charCount.toLocaleString()} characters. Maximum is 100,000. Use selector to read specific sections (e.g., {"indexRange": {"from": 0, "to": 50}}).`
        );
      }
      
      log.log(`[SPEAK_OUTLOUD] Generating audio for ${charCount} characters`);
      
      // Normalize settings
      const voice = (params.voice || 'alloy').toLowerCase();
      const speed = Math.max(0.25, Math.min(4.0, params.speed || 1.0));
      const quality = (params.quality || 'standard').toLowerCase();
      
      // Validate voice
      const validVoices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
      if (!validVoices.includes(voice)) {
        return createErrorResult(
          'Invalid voice',
          `Voice "${voice}" is not valid. Must be one of: ${validVoices.join(', ')}`
        );
      }
      
      context.onProgress?.({
        type: 'streaming_update',
        activity: `Generating audio (${charCount.toLocaleString()} characters)...`
      });
      
      // Call TTS service
      const audioResult = await ttsService.generateAudio(
        textToRead,
        voice as any,
        speed,
        quality as any,
        (progress) => {
          context.onProgress?.({
            type: 'streaming_update',
            activity: `Generating audio: ${progress.percent}% (${progress.chunks_done}/${progress.chunks_total} chunks)`
          });
        }
      );
      
      if (audioResult.error) {
        return createErrorResult('Audio generation failed', audioResult.error);
      }
      
      log.log(`[SPEAK_OUTLOUD] Audio generated: ${audioResult.duration}s, ${audioResult.chunks} chunks`);
      
      // Return result with audio data
      return createSuccessResult(
        `Generated audio for "${documentName}" (${charCount.toLocaleString()} chars, ${audioResult.duration}s, ${voice} voice)`,
        {
          type: 'audio_content',
          audio: audioResult.audio,  // Base64 encoded MP3
          duration: audioResult.duration,
          voice: voice,
          speed: speed,
          quality: quality,
          characterCount: charCount,
          documentName: documentName,
          chunks: audioResult.chunks
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            documentName,
            method: 'speak_outloud'
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[SPEAK_OUTLOUD] Error:', error);
      return createErrorResult(
        'Speech generation failed',
        error.message || 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};






